import { type TagName, Tags } from '~/api/k2/tags.autogen';

/**
 * `Stage`, `Task` and `Comment` tag ids are individual entity ids elsewhere
 * (`getTask` provides `{ Task, taskId }`, and task mutations invalidate it), so
 * a ticket id is namespaced to keep the two id spaces from overlapping.
 */
export const ticketScopedTagId = (ticketId: string) => `ticket:${ticketId}`;

export const LIST_TAG_ID = 'LIST';

export type RealtimeTag = { type: TagName; id?: string };

/**
 * Tag types whose list endpoints provide an arg-agnostic `id: 'LIST'` even
 * though every row belongs to exactly one ticket. `listTasks` is called once per
 * stage, so an unscoped `Task/LIST` invalidation refetches every cached task
 * list on every ticket.
 *
 * This is the single source of truth: `ticketScopedListTags` (used by the
 * reconnect resync in `./subscriptions`) and the rewrite in
 * `scopeListTagsToTicket` both derive from it, so widening the scoping to
 * another tag type is a one-line change here.
 */
const TICKET_SCOPED_LIST_TAG_TYPES = [
  Tags.Stage,
  Tags.Task,
  Tags.Comment,
] as const;

const isTicketScopedListTagType = new Set<string>(TICKET_SCOPED_LIST_TAG_TYPES);

/** Every ticket-scoped list tag for one ticket. */
export const ticketScopedListTags = (ticketId: string): RealtimeTag[] =>
  TICKET_SCOPED_LIST_TAG_TYPES.map((type) => ({
    type,
    id: ticketScopedTagId(ticketId),
  }));

/**
 * Rewrite the arg-agnostic `LIST` tags a `ticket-updated` event carries into
 * ticket-scoped ones, so invalidation reaches only the lists belonging to the
 * ticket the event was published for.
 *
 * `Ticket/LIST` is deliberately left alone: it means "the tickets collection
 * changed", which legitimately affects the cross-ticket `listTickets` and
 * `searchTicketsList` queries.
 */
export const scopeListTagsToTicket = (
  ticketId: string,
  tags: RealtimeTag[],
): RealtimeTag[] =>
  tags.map((tag) =>
    tag.id === LIST_TAG_ID && isTicketScopedListTagType.has(tag.type)
      ? { type: tag.type, id: ticketScopedTagId(ticketId) }
      : tag,
  );
